import { AuthGuard } from "@redotech/redo-customer-portal-app/contexts/auth";
import { portalCustomizationDisabledRouteHandle } from "@redotech/redo-customer-portal-app/customization/customization-context";
import { useTranslation } from "@redotech/redo-i18n";
import { breadcrumbSlot, useBreadcrumb } from "@redotech/redo-web/breadcrumb";
import { memo, ReactNode } from "react";
import { Outlet, RouteObject } from "react-router-dom";
import { orderTrackingIframeRoutes } from "../order-tracking-iframes/routes";
import { giftReceiptRoutes } from "./gift-receipts/routes";
import { Page } from "./page";

const ordersBreadcrumb = breadcrumbSlot();
const orderBreadcrumb = breadcrumbSlot();
const orderEditBreadcrumb = breadcrumbSlot();
const trackingBreadcrumb = breadcrumbSlot();

const OrdersBreadcrumb = memo(function OrdersBreadcrumb({
  children,
}: {
  children: ReactNode;
}) {
  const { t } = useTranslation();
  useBreadcrumb(ordersBreadcrumb, t("Orders"));
  return <>{children}</>;
});

const TrackingBreadcrumb = memo(function TrackingBreadcrumb({
  children,
}: {
  children: ReactNode;
}) {
  const { t } = useTranslation();
  useBreadcrumb(trackingBreadcrumb, t("Tracking"));
  return <>{children}</>;
});

export const orderRoutes: RouteObject[] = [
  // Temp custom order tracking iframe routes
  ...orderTrackingIframeRoutes,
  {
    handle: { breadcrumb: ordersBreadcrumb },
    element: (
      <OrdersBreadcrumb>
        <AuthGuard>
          <Outlet />
        </AuthGuard>
      </OrdersBreadcrumb>
    ),
    children: [
      {
        index: true,
        path: "",
        lazy: async () => {
          const { Orders } =
            await import("@redotech/redo-customer-portal-app/portal-four/orders/orders");
          return {
            element: (
              <Page>
                <Orders registerOnly={false} />
              </Page>
            ),
          };
        },
      },
      {
        path: ":id",
        handle: { breadcrumb: orderBreadcrumb },
        lazy: async () => {
          const { Order } = await import("./order");
          return { element: <Order breadcrumb={orderBreadcrumb} /> };
        },
        children: [
          {
            path: "",
            lazy: async () => {
              const { OrderDetails } = await import("./order-details");
              return { Component: OrderDetails };
            },
            children: ["track", "track/:fulfillmentIndex"].map((path) => ({
              handle: {
                breadcrumb: trackingBreadcrumb,
                ...portalCustomizationDisabledRouteHandle,
              },
              path,
              lazy: async () => {
                const { TrackingPageFulfillment } =
                  await import("./order-tracking/fulfillment/tracking-page-fulfillment");
                return {
                  element: (
                    <TrackingBreadcrumb>
                      <TrackingPageFulfillment />
                    </TrackingBreadcrumb>
                  ),
                };
              },
            })),
          },
          {
            path: "hsa-reimburse",
            lazy: async () => {
              const { HsaReimburse } =
                // redont-allow-dynamic-import: route-level code splitting
                await import("@redotech/redo-customer-portal-app/portal-four/orders/hsa-reimburse/hsa-reimburse");
              return { Component: HsaReimburse };
            },
          },
          {
            path: "return-method",
            lazy: async () => {
              const { ReturnMethod } =
                // redont-allow-dynamic-import: route-level code splitting
                await import("@redotech/redo-customer-portal-app/portal-four/orders/return-method/return-method");
              return { Component: ReturnMethod };
            },
          },
          {
            handle: { breadcrumb: orderEditBreadcrumb },
            path: "edit",
            lazy: async () => {
              const { OrderEdit } = await import("./order-edit");
              return {
                element: <OrderEdit breadcrumb={orderEditBreadcrumb} />,
              };
            },
          },
          {
            // TODO (JACK): This route should probably be limited to known return types and not just eat all possible routes
            path: ":returnType",
            lazy: async () => {
              const { ReturnTypeRouter } = await import("./return-type-router");
              return { Component: ReturnTypeRouter };
            },
          },
          {
            path: "register",
            lazy: async () => {
              const { Main: RegistrationPortal } =
                await import("@redotech/redo-customer-portal-app/portal-four/register/main");
              return { element: <RegistrationPortal /> };
            },
          },
        ],
      },
      {
        path: "register",
        lazy: async () => {
          const { Orders } =
            await import("@redotech/redo-customer-portal-app/portal-four/orders/index");
          return {
            element: (
              <Page>
                <Orders registerOnly />
              </Page>
            ),
          };
        },
      },
      {
        path: ":id/gift-receipts",
        element: <Outlet />,
        children: giftReceiptRoutes,
      },
    ],
  },
];
